import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Camera, X, Repeat, ArrowsClockwise } from "@phosphor-icons/react";

/* Live-only camera capture. Returns base64 JPEG (no data: prefix), client-side compressed to
 * 720 px wide @ q70. File picker is intentionally NOT exposed — must be live. */
export default function CameraCapture({ open, onClose, onCapture, label = "Live photo" }) {
  const videoRef = useRef();
  const canvasRef = useRef();
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("user"); // user | environment
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  async function start() {
    setErr(null);
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (e) { setErr("Camera permission denied or unavailable."); }
  }

  useEffect(() => { if (open) start(); return () => { try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {} }; /* eslint-disable-next-line */ }, [open, facing]);

  function shutter() {
    if (!videoRef.current) return;
    setBusy(true);
    try {
      const v = videoRef.current;
      const targetW = Math.min(720, v.videoWidth || 720);
      const ratio = targetW / (v.videoWidth || targetW);
      const targetH = Math.round((v.videoHeight || 540) * ratio);
      const c = canvasRef.current; c.width = targetW; c.height = targetH;
      const ctx = c.getContext("2d");
      ctx.drawImage(v, 0, 0, targetW, targetH);
      const dataUrl = c.toDataURL("image/jpeg", 0.7);
      const b64 = dataUrl.split(",")[1] || "";
      onCapture(b64);
    } finally { setBusy(false); }
  }

  if (!open) return null;
  return (
    <motion.div className="fixed inset-0 z-[80] flex flex-col bg-black" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="camera-capture">
      <div className="pt-safe flex items-center justify-between border-b border-white/10 px-4 py-3">
        <button onClick={onClose} className="rounded-full bg-white/10 p-2 text-white" data-testid="camera-close"><X size={18} weight="bold" /></button>
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/70">{label}</p>
        <button onClick={() => setFacing((f) => f === "user" ? "environment" : "user")} className="rounded-full bg-white/10 p-2 text-white" data-testid="camera-flip"><Repeat size={18} weight="bold" /></button>
      </div>
      <div className="relative flex-1 overflow-hidden bg-black">
        {err ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-[13px] text-neon-pink">{err}</p>
            <button onClick={start} className="btn-ghost"><ArrowsClockwise size={14} weight="bold" className="mr-1" /> Retry</button>
          </div>
        ) : (
          <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>
      <div className="pb-safe flex items-center justify-center border-t border-white/10 bg-black px-4 py-4">
        <button onClick={shutter} disabled={!!err || busy} data-testid="camera-shutter"
          className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-white bg-white/10 transition active:scale-95 disabled:opacity-40">
          <span className="block h-12 w-12 rounded-full bg-white" />
        </button>
      </div>
    </motion.div>
  );
}
